import * as THREE from "three";
import { inked, toon } from "../toon";
import type { SkaterLook } from "./look";
import { facets } from "./geom";

// The scooter (#139): a pro stunt scooter in the yaw group's space (front
// wheel toward +z, ground at y 0). Like the BMX it's two halves that turn
// about the steering axis, so tricks can spin them apart: `deck` (the deck,
// neck and back wheel; a tailwhip spins it round the stem) and `front` (the
// bars, stem, fork and front wheel; a barspin spins it). Both halves have
// their origin on the steering axis. Built only once someone gets on one.

/** Wheel radius (m): 110 mm wheels. */
export const SCOOTER_WHEEL_R = 0.055;
/** The steering axis's place along the scooter, from its middle (z): over the front wheel. */
export const SCOOTER_HEAD_Z = 0.3;
/** The back wheel's hub, from the steering axis (z). */
export const SCOOTER_REAR_HUB = -0.58;
/** The deck's top (y) and its two ends along the scooter (z, from its middle): where the feet go and where it grinds. */
export const SCOOTER_DECK_Y = 0.1;
export const SCOOTER_DECK_THICK = 0.035;
export const SCOOTER_DECK_FRONT = 0.2;
export const SCOOTER_DECK_BACK = -0.27;
/** The grips, in the front half's space (from the steering axis), one each side (±x). */
export const SCOOTER_GRIP = new THREE.Vector3(0.27, 0.86, -0.02);

export interface Scooter {
  root: THREE.Group;
  deck: THREE.Group;
  front: THREE.Group;
  /** The two wheels, turned about x as it rolls. */
  wheels: THREE.Object3D[];
}

/** A tube from a to b (in the group's space), `r` thick. */
function tube(a: THREE.Vector3, b: THREE.Vector3, r: number, mat: THREE.Material): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(r, r, a.distanceTo(b), facets(10));
  const m = inked(geo, mat, 0.008);
  m.position.copy(a).add(b).multiplyScalar(0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), b.clone().sub(a).normalize());
  return m;
}

function wheel(tyre: THREE.Material, core: THREE.Material): THREE.Group {
  const w = new THREE.Group();
  const t = inked(new THREE.TorusGeometry(SCOOTER_WHEEL_R - 0.015, 0.015, facets(8), facets(20)), tyre, 0.006);
  t.rotation.y = Math.PI / 2;
  // A hollow core with spokes, so the spin shows.
  const c = new THREE.Mesh(new THREE.CylinderGeometry(SCOOTER_WHEEL_R - 0.025, SCOOTER_WHEEL_R - 0.025, 0.022, facets(12)), core);
  c.rotation.z = Math.PI / 2;
  const spoke = new THREE.Mesh(new THREE.BoxGeometry(0.026, (SCOOTER_WHEEL_R - 0.02) * 2, 0.012), tyre);
  w.add(t, c, spoke);
  return w;
}

export function buildScooter(look: SkaterLook): Scooter {
  const paint = toon(look.deck, { roughness: 0.4 }, "paint");
  const metal = toon("#b9c0cc", { roughness: 0.3, metalness: 0.7 }, "metal");
  const dark = toon("#1f1f26", { roughness: 0.85 });
  const core = toon(look.wheels, { roughness: 0.4 }, "plastic");

  const root = new THREE.Group();
  root.name = "scooter";
  const deck = new THREE.Group();
  const front = new THREE.Group();
  deck.position.z = SCOOTER_HEAD_Z;
  front.position.z = SCOOTER_HEAD_Z;
  root.add(deck, front);

  const R = SCOOTER_WHEEL_R;
  const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);
  // The deck, measured from the steering axis: a flat plank with griptape on
  // top, a neck sweeping up to the headtube, and the back wheel in a slot.
  const len = SCOOTER_DECK_FRONT - SCOOTER_DECK_BACK;
  const midZ = (SCOOTER_DECK_FRONT + SCOOTER_DECK_BACK) / 2 - SCOOTER_HEAD_Z;
  const plank = inked(new THREE.BoxGeometry(0.12, SCOOTER_DECK_THICK, len), paint, 0.008);
  plank.position.set(0, SCOOTER_DECK_Y - SCOOTER_DECK_THICK / 2, midZ);
  const grip = new THREE.Mesh(new THREE.BoxGeometry(0.11, 0.004, len - 0.02), dark);
  grip.position.set(0, SCOOTER_DECK_Y + 0.002, midZ);
  const frontEnd = SCOOTER_DECK_FRONT - SCOOTER_HEAD_Z;
  const neckFoot = v(0, SCOOTER_DECK_Y - 0.01, frontEnd);
  const headBottom = v(0, 0.2, 0);
  const headTop = v(0, 0.3, 0);
  deck.add(plank, grip, tube(neckFoot, headBottom, 0.028, paint), tube(headBottom, headTop, 0.03, paint));
  const back = wheel(dark, core);
  back.position.set(0, R, SCOOTER_REAR_HUB);
  for (const side of [1, -1]) {
    const drop = inked(new THREE.BoxGeometry(0.008, 0.06, 0.09), metal, 0.004);
    drop.position.set(side * 0.03, R + 0.02, SCOOTER_REAR_HUB + 0.02);
    deck.add(drop);
  }
  deck.add(back);

  // The front: fork down to the hub on the axis, a T-bar on top.
  const hub = v(0, R, 0);
  for (const side of [1, -1]) front.add(tube(v(side * 0.03, 0.2, 0), hub.clone().add(v(side * 0.03, 0, 0)), 0.01, metal));
  const barY = SCOOTER_GRIP.y;
  const gz = SCOOTER_GRIP.z;
  front.add(
    tube(v(0, 0.3, 0), v(0, barY, gz), 0.016, metal),
    tube(v(-SCOOTER_GRIP.x + 0.06, barY, gz), v(SCOOTER_GRIP.x - 0.06, barY, gz), 0.013, metal),
  );
  for (const side of [1, -1]) {
    const g = inked(new THREE.CylinderGeometry(0.018, 0.018, 0.12, facets(10)), dark, 0.006);
    g.rotation.z = Math.PI / 2;
    g.position.set(side * SCOOTER_GRIP.x, barY, gz);
    front.add(g);
  }
  const fw = wheel(dark, core);
  fw.position.copy(hub);
  front.add(fw);
  return { root, deck, front, wheels: [back, fw] };
}
